/**
 * My Purchases Page
 * Shows leads the user has purchased with full PII revealed
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Divider } from 'primereact/divider';
import { Avatar } from 'primereact/avatar';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { LeadGradeBadge } from './components';
import {
    marketplaceService,
    formatLoanAmount,
    formatTimeAgo,
    getLoanProductConfig
} from '../../services/marketplaceService';

const MyPurchases = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [purchases, setPurchases] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, completed: 0 });
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const statusOptions = [
        { label: 'All Purchases', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Completed', value: 'completed' }
    ];

    // Fetch purchases
    const fetchPurchases = async () => {
        setLoading(true);
        try {
            const params = statusFilter !== 'all' ? { status: statusFilter } : {};
            const response = await marketplaceService.getMyPurchases(params);
            if (response.success) {
                setPurchases(response.data?.purchases || []);
                if (response.data?.stats) {
                    setStats(response.data.stats);
                }
            }
        } catch (error) {
            console.error('Failed to fetch purchases:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load your purchases'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchases();
    }, [statusFilter]);

    // Filter by search
    const filteredPurchases = purchases.filter(p => {
        if (!searchTerm) return true;
        const lead = p.lead || {};
        const searchLower = searchTerm.toLowerCase();
        return (
            lead.full_name?.toLowerCase().includes(searchLower) ||
            lead.phone?.includes(searchTerm) ||
            lead.email?.toLowerCase().includes(searchLower)
        );
    });

    // Loading skeleton
    const renderSkeleton = () => (
        <div className="grid">
            {[1, 2, 3].map(i => (
                <div key={i} className="col-12">
                    <Card className="mb-3">
                        <div className="flex gap-4">
                            <Skeleton shape="circle" size="4rem" />
                            <div className="flex-1">
                                <Skeleton width="40%" height="1.5rem" className="mb-2" />
                                <Skeleton width="60%" className="mb-2" />
                                <Skeleton width="30%" />
                            </div>
                        </div>
                    </Card>
                </div>
            ))}
        </div>
    );

    // Empty state
    const renderEmpty = () => (
        <div className="flex flex-column align-items-center justify-content-center py-8">
            <i className="pi pi-shopping-bag text-5xl text-300 mb-4" />
            <h3 className="text-600 mb-2">No Purchases Yet</h3>
            <p className="text-500 mb-4">You haven't purchased any leads from the marketplace.</p>
            <Button
                label="Browse Marketplace"
                icon="pi pi-search"
                onClick={() => navigate('/app/marketplace')}
            />
        </div>
    );

    // Purchase card component
    const PurchaseCard = ({ purchase }) => {
        const lead = purchase.lead || {};
        const seller = purchase.seller || {};
        const productConfig = getLoanProductConfig(lead.product_type);

        return (
            <Card className="mb-3 shadow-2">
                {/* Header */}
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-4">
                    <div className="flex align-items-center gap-3">
                        <Tag value={productConfig.label} icon={`pi ${productConfig.icon}`} severity="info" />
                        <LeadGradeBadge grade={purchase.quality_grade} size="small" />
                        <Tag
                            value={purchase.collaboration_status || 'Active'}
                            severity={purchase.collaboration_status === 'completed' ? 'success' : 'warning'}
                        />
                    </div>
                    <span className="text-500 text-sm">Purchased {formatTimeAgo(purchase.purchased_at)}</span>
                </div>

                {/* Borrower Details (Full PII) */}
                <div className="surface-50 border-round p-4 mb-4">
                    <div className="flex align-items-center gap-2 mb-3">
                        <i className="pi pi-user text-primary" />
                        <h4 className="text-900 font-bold m-0">Borrower Details</h4>
                        <Tag value="Full Access" icon="pi pi-unlock" severity="success" className="ml-auto" />
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="mb-3">
                                <div className="text-500 text-sm mb-1">Full Name</div>
                                <div className="text-900 font-semibold text-lg">{lead.full_name || 'N/A'}</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="mb-3">
                                <div className="text-500 text-sm mb-1">Phone</div>
                                <a href={`tel:${lead.phone}`} className="text-primary font-semibold text-lg no-underline hover:underline">
                                    <i className="pi pi-phone mr-2" />{lead.phone || 'N/A'}
                                </a>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="mb-3">
                                <div className="text-500 text-sm mb-1">Email</div>
                                <a href={`mailto:${lead.email}`} className="text-primary font-semibold no-underline hover:underline">
                                    <i className="pi pi-envelope mr-2" />{lead.email || 'N/A'}
                                </a>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="mb-3">
                                <div className="text-500 text-sm mb-1">PAN</div>
                                <div className="text-900 font-semibold">{lead.pan || 'N/A'}</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="mb-3">
                                <div className="text-500 text-sm mb-1">Address</div>
                                <div className="text-900">{lead.address || `${lead.city}, ${lead.state}`}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lead & Financial Details */}
                <Accordion>
                    <AccordionTab header="Lead & Financial Details">
                        <div className="grid">
                            <div className="col-6 md:col-3">
                                <div className="text-500 text-sm mb-1">Loan Amount</div>
                                <div className="text-900 font-bold text-xl">{formatLoanAmount(lead.loan_amount)}</div>
                            </div>
                            <div className="col-6 md:col-3">
                                <div className="text-500 text-sm mb-1">Employment</div>
                                <div className="text-900 font-semibold">{lead.employment_type || 'N/A'}</div>
                            </div>
                            <div className="col-6 md:col-3">
                                <div className="text-500 text-sm mb-1">CIBIL Score</div>
                                <div className="text-900 font-semibold">{lead.cibil_score || 'N/A'}</div>
                            </div>
                            <div className="col-6 md:col-3">
                                <div className="text-500 text-sm mb-1">Income</div>
                                <div className="text-900 font-semibold">{formatLoanAmount(lead.monthly_income || lead.annual_turnover)}</div>
                            </div>
                        </div>
                    </AccordionTab>

                    <AccordionTab header="Documents">
                        {lead.documents?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {lead.documents.map((doc, idx) => (
                                    <Button
                                        key={idx}
                                        label={doc.name || doc.type}
                                        icon="pi pi-file"
                                        className="p-button-outlined p-button-sm"
                                        onClick={() => window.open(doc.url, '_blank')}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-500 m-0">No documents available</p>
                        )}
                    </AccordionTab>

                    <AccordionTab header="Seller Contact">
                        <div className="flex align-items-center gap-4">
                            <Avatar
                                image={seller.profile_photo_url_full || seller.profile_photo_url || seller.avatar}
                                icon="pi pi-user"
                                size="large"
                                shape="circle"
                                className="bg-primary"
                            />
                            <div>
                                <div className="font-semibold text-900">{seller.full_name || seller.display_name || 'Agent'}</div>
                                <div className="text-500 text-sm">{seller.current_company || seller.company_name}</div>
                                {seller.phone && (
                                    <a href={`tel:${seller.phone}`} className="text-primary text-sm">
                                        <i className="pi pi-phone mr-1" />{seller.phone}
                                    </a>
                                )}
                            </div>
                            <Button
                                label="Contact"
                                icon="pi pi-comments"
                                className="p-button-outlined ml-auto"
                                onClick={() => window.open(`mailto:${seller.email}`, '_blank')}
                            />
                        </div>
                    </AccordionTab>
                </Accordion>

                {/* Collaboration Info */}
                <Divider />
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                    <div className="flex gap-4 text-sm">
                        <div>
                            <span className="text-500">Your Commission: </span>
                            <span className="font-bold text-green-600">{purchase.your_commission || purchase.collaboration_commission_percentage}%</span>
                        </div>
                        <div>
                            <span className="text-500">Deal ID:</span>
                            <span className="font-mono">{purchase.collaboration_id?.slice(0, 8)}...</span>
                        </div>
                    </div>
                    <Button
                        label="Download Lead Data"
                        icon="pi pi-download"
                        className="p-button-text p-button-sm"
                        onClick={() => {
                            toast.current?.show({
                                severity: 'info',
                                summary: 'Downloading',
                                detail: 'Lead data export started'
                            });
                        }}
                    />
                </div>
            </Card>
        );
    };

    return (
        <div className="my-purchases">
            <Toast ref={toast} />

            {/* Header */}
            <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="text-900 font-bold m-0 mb-1">My Purchases</h2>
                    <p className="text-600 m-0">Leads you've purchased with full access</p>
                </div>
                <Button
                    label="Browse More"
                    icon="pi pi-search"
                    onClick={() => navigate('/app/marketplace')}
                />
            </div>

            {/* Stats Cards */}
            <div className="grid mb-4">
                {[
                    { label: 'Total Purchases', value: stats.total, icon: 'pi-shopping-bag', color: 'blue' },
                    { label: 'Active', value: stats.active, icon: 'pi-spinner', color: 'orange' },
                    { label: 'Completed', value: stats.completed, icon: 'pi-check-circle', color: 'green' }
                ].map((stat, idx) => (
                    <div key={idx} className="col-12 md:col-4">
                        <Card className="shadow-1 text-center">
                            <i className={`pi ${stat.icon} text-2xl text-${stat.color}-500 mb-2`} />
                            <div className="text-2xl font-bold text-900">{stat.value}</div>
                            <div className="text-500 text-sm">{stat.label}</div>
                        </Card>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <Card className="mb-4">
                <div className="flex flex-column md:flex-row gap-3">
                    <Dropdown
                        value={statusFilter}
                        options={statusOptions}
                        onChange={(e) => setStatusFilter(e.value)}
                        className="w-full md:w-15rem"
                    />
                    <span className="p-input-icon-left flex-1">
                        <i className="pi pi-search" />
                        <InputText
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name, phone, or email..."
                            className="w-full"
                        />
                    </span>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-outlined"
                        onClick={fetchPurchases}
                        loading={loading}
                        tooltip="Refresh"
                    />
                </div>
            </Card>

            {/* Purchases List */}
            {loading ? renderSkeleton() : filteredPurchases.length === 0 ? renderEmpty() : (
                <div>
                    {filteredPurchases.map(purchase => (
                        <PurchaseCard key={purchase.id} purchase={purchase} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyPurchases;
